import React, { useMemo } from 'react';

import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Icon } from '@/components/shared/element/icons';
import { Line } from '@/components/shared/element/line';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Image } from '@/components/shared/media/image';
import { ImageProps } from '@/components/shared/media/image/Image.props';
import { styled } from '@/stitches.config';
import { IButton } from '@/types/shared';

const HeadlineStyled = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  rowGap: '$space-6',
  variants: {
    isDescription: {
      true: {
        rowGap: '$space-6',
      },
    },
    isLine: {
      true: {
        rowGap: '$space-0',
      },
    },
    isCenter: {
      true: {
        textAlign: 'center',
      },
    },
  },
});

interface HeadingItemProps extends React.ComponentProps<typeof HeadlineStyled> {
  /**
   * The lead text.
   */
  lead?: string;

  /**
   * The title text.
   */
  title: string;

  /**
   * The description text.
   */
  description?: string;

  /**
   * The features array.
   */
  features?: string[];

  /**
   * The remark text.
   */
  remark?: string;

  /**
   * The highlight text.
   */
  highlight?: string;

  /**
   * The buttons array.
   */
  buttons?: IButton[];

  /**
   * The logo image.
   */
  logo?: ImageProps;

  /**
   * The logo fake part image.
   */
  logoFakePart?: ImageProps;

  /**
   * The flag indicating if the theme is dark.
   */
  isDarkTheme?: boolean;

  /**
   * The theme.
   */
  theme?: 'blue' | 'green' | 'dark';

  /**
   * The flag indicating if the content should be centered on mobile.
   */
  isAlignCenterOnMobile?: boolean;

  /**
   * The flag indicating if the component is a line.
   */
  isLine?: boolean;

  /**
   * The flag indicating if the component is centered.
   */
  isCenter?: boolean;

  /**
   * The data track attribute.
   */
  dataTrack?: string;

  /**
   * The data track section attribute.
   */
  dataTrackSection?: string;

  /**
   * The data track value attribute.
   */
  dataTrackValue?: string;

  /**
   * The data test attribute.
   */
  dataTest?: string;

  /**
   * The size of the component.
   */
  size?: 'default' | 'sm';
}

/**
 * Renders a heading item component with various text elements, buttons, and images.
 *
 * @param {HeadingItemProps} props - The props for the HeadingItem component.
 * @param {string} props.lead - The lead text for the component.
 * @param {string} props.title - The title text for the component.
 * @param {string} props.description - The description text for the component.
 * @param {string[]} props.features - The features for the component.
 * @param {string} props.remark - The remark text for the component.
 * @param {string} props.highlight - The highlight text for the component.
 * @param {ButtonProps[]} [props.buttons=[]] - The buttons for the component.
 * @param {ImageProps} props.logo - The logo image for the component.
 * @param {ImageProps} props.logoFakePart - The logo fake part image for the component.
 * @param {boolean} [props.isDarkTheme=false] - Indicates if the theme is dark.
 * @param {string} props.theme - The theme for the component.
 * @param {boolean} [props.isAlignCenterOnMobile=false] - Indicates if the component should be centered on mobile.
 * @param {boolean} [props.isLine=false] - Indicates if the component should have a line.
 * @param {boolean} [props.isCenter=false] - Indicates if the component should be centered.
 * @param {string} [props.dataTrack] - The data track attribute for the component.
 * @param {string} [props.dataTrackSection] - The data track section attribute for the component.
 * @param {string} [props.dataTrackValue] - The data track value attribute for the component.
 * @param {string} [props.dataTest='heading'] - The data test attribute for the component.
 * @param {('default' | 'sm')} [props.size='default'] - The size of the component.
 * @param {...React.HTMLAttributes<HTMLDivElement>} props.rest - Additional attributes for the component.
 * @return {ReactElement} The rendered HeadingItem component.
 */
export const HeadingItem: React.FC<HeadingItemProps> = ({
  lead,
  title,
  description,
  features,
  remark,
  highlight,
  buttons = [],
  logo,
  logoFakePart,
  isDarkTheme = false,
  theme,
  isAlignCenterOnMobile = false,
  isLine = false,
  isCenter = false,
  dataTrack,
  dataTrackSection,
  dataTrackValue,
  dataTest = 'heading',
  size = 'default',
  css,
  ...rest
}: HeadingItemProps) => {
  const TEXT_LEAD = useMemo(() => {
    if (isDarkTheme) {
      return 'text-white';
    } else {
      switch (theme) {
        case 'blue':
          return 'text-blue';
        case 'green':
          return 'text-green';
        case 'dark':
          return 'text-gray-darkest';
        default:
          return 'text-red';
      }
    }
  }, [isDarkTheme, theme]);

  const getTitleVariant = () =>
    isDarkTheme ? 'text-white' : 'text-gray-darkest';

  const getHighlightVariant = () =>
    isDarkTheme ? 'text-white' : isCenter ? 'text-red' : 'text-gray-darkest';

  const getButtonVariant = () => (isDarkTheme ? 'button-white' : 'button-red');

  // Checks if there are buttons and if any of them have a non-empty label.
  const hasButtons = buttons.some((item) => item.label !== '');

  return (
    <HeadlineStyled
      {...rest}
      isLine={isLine}
      isDescription={!!description}
      isCenter={isCenter}
      data-test={dataTest}
      data-nosnippet
      css={
        isAlignCenterOnMobile
          ? {
              '@maxlg': {
                placeItems: 'center',
                textAlign: 'center',
              },
            }
          : css
      }
    >
      {!!(lead || title) && (
        <Grid>
          {!!lead && (
            <Text
              as="h3"
              size="text-body-1"
              font="bold"
              variant={TEXT_LEAD}
              wrap
              dangerouslySetInnerHTML={{ __html: lead }}
              data-test={`${dataTest}_preTitle`}
              css={{ marginBottom: '$space-2' }}
            />
          )}
          {!!title && (
            <Text
              as="h2"
              size={size === 'default' ? 'text-h2' : 'text-h3'}
              variant={getTitleVariant()}
              wrap
              dangerouslySetInnerHTML={{ __html: title }}
              data-test={`${dataTest}_title`}
            />
          )}
        </Grid>
      )}
      {!!isLine && <Line />}
      {!!features && features.length > 0 && (
        <Grid rowGap="2">
          {features.map((feature, index) => (
            <Flex key={index} columnGap="2">
              <Icon icon="correct" color="icon-red" />
              <Text
                size="text-body-1"
                variant={getTitleVariant()}
                wrap
                dangerouslySetInnerHTML={{
                  __html: feature,
                }}
              />
            </Flex>
          ))}
        </Grid>
      )}
      {!!(
        description ||
        remark ||
        highlight ||
        logoFakePart?.src ||
        logo?.src ||
        hasButtons
      ) && (
        <Grid rowGap="6">
          {!!(
            description ||
            remark ||
            highlight ||
            logoFakePart?.src ||
            logo?.src
          ) && (
            <Grid rowGap={isCenter ? '2' : '6'}>
              {!!description && (
                <Text
                  as="div"
                  size="text-body-1"
                  font={isLine || isCenter ? 'bold' : 'base'}
                  variant={getTitleVariant()}
                  wrap
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  data-test={`${dataTest}_description`}
                  className={`text-${theme ?? 'description'}`}
                  css={isCenter ? { maxWidth: 480, mx: 'auto' } : undefined}
                />
              )}
              {!!highlight && (
                <Text
                  as="div"
                  size="text-sub-h1"
                  font="bold"
                  variant={getHighlightVariant()}
                  wrap
                  dangerouslySetInnerHTML={{ __html: highlight }}
                  data-test={`${dataTest}_highlight`}
                />
              )}
              {!!(logoFakePart?.src || logo?.src) && (
                <Flex
                  direction={{ '@initial': 'column', '@lg': 'row' }}
                  alignItems="center"
                  columnGap="4"
                  rowGap="2"
                >
                  {!!logoFakePart?.src && (
                    <Image
                      priority={logoFakePart?.priority}
                      isMaxWidth
                      src={logoFakePart?.src}
                      alt={logoFakePart?.alt}
                      width={logoFakePart?.width}
                      height={logoFakePart?.height}
                      layout="responsive"
                    />
                  )}
                  {!!logo?.src && (
                    <Image
                      priority={logo?.priority}
                      isMaxWidth
                      src={logo?.src}
                      alt={logo?.alt}
                      width={logo?.width}
                      height={logo?.height}
                      layout="responsive"
                    />
                  )}
                </Flex>
              )}
              {!!remark && (
                <Text
                  as="div"
                  size="text-caption"
                  variant={getTitleVariant()}
                  wrap
                  dangerouslySetInnerHTML={{ __html: remark }}
                  data-test={`${dataTest}_remark`}
                />
              )}
            </Grid>
          )}
          {!!hasButtons && (
            <Box>
              <ButtonGroup
                justify={isCenter ? 'center' : undefined}
                buttons={buttons}
                variant={getButtonVariant()}
                isMinWidth
                tracking={{
                  dataTrack: dataTrack ?? '',
                  dataTrackSection: dataTrackSection,
                  dataTrackValue: dataTrackValue,
                }}
                test={{
                  dataTest: `${dataTest}_button`,
                }}
              />
            </Box>
          )}
        </Grid>
      )}
    </HeadlineStyled>
  );
};

HeadingItem.displayName = 'HeadingItem';
